/**
 * The providers and models ATLAS is qualified to call.
 *
 * A registry exists because `provider` and `model` were free strings on
 * `PromptModelConfig`: `z.string().min(1)` accepted `"anthropc"` or a model that was
 * never qualified, saved it, froze it into a published version, and only failed at
 * the provider — mid-execution, after the run had started. Naming the allowed set
 * turns that into a save-time refusal and gives the authoring UI something to offer
 * instead of a text box.
 *
 * Deliberately CODE, not data. A model is qualified by someone reading its published
 * rate and checking the calling path works, which is a release-time act — unlike an
 * axis value or a prompt list, no tenant administrator can do it. This is the same
 * reasoning that keeps `HookCode` and `UserRole` enums (see CLAUDE.md).
 */

/** An external service ATLAS sends generation work to. */
export enum AiProvider {
	/** Text and structured output. */
	ANTHROPIC = 'anthropic',
	/** Text-to-image and image expansion. */
	MAGNIFIC = 'magnific',
}

/** What a model can be asked to do — the reason a caller picks one over another. */
export enum ModelCapability {
	TEXT = 'text',
	IMAGE = 'image',
}

export interface RegisteredModel {
	/** The exact id sent to the provider. */
	id: string
	provider: AiProvider
	capability: ModelCapability
	/** Human label for a picker. */
	label: string
	/**
	 * Superseded by this model id, and never invoked.
	 *
	 * A published prompt version can outlive the model it was published against, so
	 * a retired entry stays here rather than being deleted: `resolveModel` needs the
	 * replacement, and dropping the row would make an old version's model merely
	 * "unrecognised" and lose the mapping.
	 */
	retiredFor?: string
}

/**
 * Every model ATLAS may invoke, plus the retired ones it knows how to replace.
 *
 * Anthropic ids are dated releases; Magnific's are the engine names pinned into its
 * endpoint paths (`/ai/text-to-image/flux-dev`, `/ai/image-expand/flux-pro`), which
 * is why they carry no date.
 */
export const REGISTERED_MODELS: readonly RegisteredModel[] = [
	{
		id: 'claude-sonnet-4-5-20250929',
		provider: AiProvider.ANTHROPIC,
		capability: ModelCapability.TEXT,
		label: 'Sonnet 4.5',
	},
	{
		id: 'claude-haiku-4-5-20251001',
		provider: AiProvider.ANTHROPIC,
		capability: ModelCapability.TEXT,
		label: 'Haiku 4.5',
	},
	{
		id: 'claude-opus-5',
		provider: AiProvider.ANTHROPIC,
		capability: ModelCapability.TEXT,
		label: 'Opus 5',
	},
	{
		id: 'claude-sonnet-5',
		provider: AiProvider.ANTHROPIC,
		capability: ModelCapability.TEXT,
		label: 'Sonnet 5',
	},
	{
		id: 'flux-dev',
		provider: AiProvider.MAGNIFIC,
		capability: ModelCapability.IMAGE,
		label: 'Flux Dev — text to image',
	},
	{
		id: 'flux-pro',
		provider: AiProvider.MAGNIFIC,
		capability: ModelCapability.IMAGE,
		label: 'Flux Pro — image expand',
	},

	// Retired. Kept so a frozen prompt version naming one still resolves to a
	// deliberate replacement rather than falling through to the generic fallback.
	{
		id: 'claude-3-opus-20240229',
		provider: AiProvider.ANTHROPIC,
		capability: ModelCapability.TEXT,
		label: 'Opus 3 (retired)',
		retiredFor: 'claude-sonnet-4-5-20250929',
	},
	{
		id: 'claude-3-sonnet-20240229',
		provider: AiProvider.ANTHROPIC,
		capability: ModelCapability.TEXT,
		label: 'Sonnet 3 (retired)',
		retiredFor: 'claude-sonnet-4-5-20250929',
	},
	{
		id: 'claude-3-haiku-20240307',
		provider: AiProvider.ANTHROPIC,
		capability: ModelCapability.TEXT,
		label: 'Haiku 3 (retired)',
		retiredFor: 'claude-haiku-4-5-20251001',
	},
] as const

const BY_ID = new Map(REGISTERED_MODELS.map((m) => [m.id, m]))

/** One model by its provider id, or `undefined` when it was never registered. */
export const findModel = (id: string): RegisteredModel | undefined => BY_ID.get(id)

/**
 * Models an author may CHOOSE, which excludes the retired ones.
 *
 * Separate from `REGISTERED_MODELS` because a retired model must still resolve for a
 * frozen version while never appearing in a picker — offering one would invite an
 * author to select something that gets substituted the moment it runs.
 */
export const selectableModels = (capability?: ModelCapability): RegisteredModel[] =>
	REGISTERED_MODELS.filter(
		(m) => m.retiredFor === undefined && (capability === undefined || m.capability === capability),
	)

/** Whether this id names a model ATLAS is qualified to call, retired included. */
export const isRegisteredModel = (id: string): boolean => BY_ID.has(id)

/** Whether this names a provider ATLAS integrates with. */
export const isKnownProvider = (provider: string): provider is AiProvider =>
	Object.values(AiProvider).includes(provider as AiProvider)
